import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Grid,
  Button,
  Select,
  FormControl,
  MenuItem,
  InputLabel,
  TextField,
  InputAdornment,
  Box,
  Typography,
  Paper,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import Modal from '@mui/material/Modal';

import { useApiGet } from '../../utils/httpClientTs';
import {
  searchUsers,
  getMyPermissions,
  advancedSearchUsers,
} from '../../endpoints/usersTs';
import { getSpds } from '../../endpoints/annualReports';
import { UserListItem, UserSearchRecord } from '../../types/users';
import { SortOrder } from '../../types/table';
import ServerSideTable, {
  ServerSideTableColumn,
} from '../../Components/ServerSideTable';
import SPDSelector from '../../Components/SPDSelector';
import UserCreateModal from '../../Components/UserCreateModal';

const UserList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const updateSearchParams = useCallback(
    (key: string, value: string) => {
      const newSearchParams = new URLSearchParams(searchParams);
      if (!value || value === '') {
        newSearchParams.delete(key);
      } else {
        newSearchParams.set(key, value);
      }
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const [order, setOrder] = useState<SortOrder>('asc');
  const [orderBy, setOrderBy] = useState<keyof UserListItem>('last_name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [delaySearch, setDelaySearch] = useState(
    () => searchParams.get('search') || ''
  );
  const [userType, setUserType] = useState(
    () => searchParams.get('type') || 'all-members'
  );
  const [spd, setSpd] = useState(searchParams.get('spd') || '');
  const [showFilters, setShowFilters] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Fetch SPD data for display names
  const { data: spdList } = useApiGet(
    ['spds', true],
    () => getSpds({ all: true }),
    {
      enabled: true,
      retry: 1,
    }
  );

  // Current user permissions (for Advanced Search button) - use international scope
  const { data: myPermissions } = useApiGet<string[]>(
    ['my-permissions', 'international'],
    () => getMyPermissions({ international: '1' }),
    { enabled: true, retry: 1 }
  );
  const hasAdvancedSearchPermission = Boolean(
    myPermissions?.some(
      (p) =>
        p === 'users:advanced-search' ||
        p.includes('users:advanced-search') ||
        p === 'ad06eaf1b0ae44fe884d2c0235e0a366,users:advanced-search'
    )
  );

  // Advanced search: modal and results
  const [advancedSearchModalOpen, setAdvancedSearchModalOpen] = useState(false);
  const [advFirstName, setAdvFirstName] = useState('');
  const [advLastName, setAdvLastName] = useState('');
  const [advMemberId, setAdvMemberId] = useState('');
  const [advEmail, setAdvEmail] = useState('');
  const [advPhone, setAdvPhone] = useState('');
  const [isShowingAdvancedResults, setIsShowingAdvancedResults] =
    useState(false);
  const [advancedSearchData, setAdvancedSearchData] = useState<{
    data: UserListItem[];
    pagination: {
      page: number;
      per_page: number;
      total_count: number;
      total_pages: number;
      has_next: boolean;
      has_prev: boolean;
    };
    can_add_user?: boolean;
  } | null>(null);
  const [advancedSearchPage, setAdvancedSearchPage] = useState(0);
  const [advancedSearchRowsPerPage, setAdvancedSearchRowsPerPage] =
    useState(25);
  const [advancedSearchForm, setAdvancedSearchForm] = useState<{
    first_name: string;
    last_name: string;
    member_id: string;
    email: string;
    phone: string;
  }>({
    first_name: '',
    last_name: '',
    member_id: '',
    email: '',
    phone: '',
  });
  const [advancedSearchLoading, setAdvancedSearchLoading] = useState(false);

  // Track previous values to prevent loops
  const prevSearch = useRef(search);
  const prevUserType = useRef(userType);
  const prevSpd = useRef(spd);

  // Handle URL parameter changes (only when URL changes externally)
  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    const urlType = searchParams.get('type') || 'all-members';
    const urlSpd = searchParams.get('spd') || '';

    // Update state from URL if different
    if (urlSearch !== search) {
      setSearch(urlSearch);
      setDelaySearch(urlSearch);
    }
    if (urlType !== userType) {
      setUserType(urlType);
    }
    if (urlSpd !== spd) {
      setSpd(urlSpd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // Only depend on searchParams to avoid loops

  // Update URL when search changes (debounced)
  useEffect(() => {
    if (prevSearch.current !== search) {
      prevSearch.current = search;
      const urlSearch = searchParams.get('search') || '';
      if (search !== urlSearch) {
        updateSearchParams('search', search);
      }
    }
  }, [search, updateSearchParams, searchParams]);

  // Update URL when userType changes and reset pagination
  useEffect(() => {
    if (prevUserType.current !== userType) {
      prevUserType.current = userType;
      const urlType = searchParams.get('type') || 'all-members';
      if (userType !== urlType) {
        updateSearchParams('type', userType);
        setPage(0); // Reset to first page when filter changes
      }
    }
  }, [userType, updateSearchParams, searchParams]);

  // Update URL when spd changes and reset pagination
  useEffect(() => {
    if (prevSpd.current !== spd) {
      prevSpd.current = spd;
      const urlSpd = searchParams.get('spd') || '';
      if (spd !== urlSpd) {
        updateSearchParams('spd', spd);
        setPage(0); // Reset to first page when filter changes
      }
    }
  }, [spd, updateSearchParams, searchParams]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearch(delaySearch);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [delaySearch]);

  // Map userType to member_status for Go server
  const mapUserTypeToMemberStatus = (
    userType: string
  ):
    | 'active'
    | 'inactive'
    | 'deceased'
    | 'non-members'
    | 'all'
    | 'all-members' => {
    switch (userType) {
      case 'all-members':
        return 'all-members';
      case 'all':
        return 'all';
      case 'member-active':
        return 'active';
      case 'member-inactive':
        return 'inactive';
      case 'deceased':
        return 'deceased';
      case 'non-member':
        return 'non-members';
      default:
        return 'all-members';
    }
  };

  const { data: rawData, isFetching } = useApiGet(
    ['users', rowsPerPage, page, search, userType, spd],
    () =>
      searchUsers({
        search: search || undefined,
        member_status: mapUserTypeToMemberStatus(userType),
        spd_chapter_id: spd || undefined,
        page: page, // 0-based for Go server
        per_page: rowsPerPage,
      }),
    {
      enabled: !isShowingAdvancedResults,
      retry: 1,
    }
  );

  // Helper function to format member status for display
  const formatMemberStatus = (memberStatus: string): string => {
    switch (memberStatus) {
      case 'active':
        return 'Active';
      case 'inactive':
        return 'Inactive';
      case 'deceased':
        return 'Deceased';
      case 'non-member':
        return 'Non-Member';
      default:
        return memberStatus;
    }
  };

  // Transform UserSearchRecord to UserListItem format
  const data = React.useMemo(() => {
    if (!rawData) return null;

    // Transform UserSearchRecord to UserListItem format
    const transformedData: UserListItem[] = rawData.data.map(
      (record: UserSearchRecord) => ({
        id: record.id,
        first_name: record.first_name,
        last_name: record.last_name,
        user_type: formatMemberStatus(record.member_status),
        last_chapter: record.current_chapter || '',
      })
    );

    // Apply client-side sorting if needed (Go server doesn't support orderBy/order)
    let sortedData = transformedData;
    if (orderBy) {
      sortedData = [...transformedData].sort((a, b) => {
        const aValue = a[orderBy];
        const bValue = b[orderBy];
        if (aValue < bValue) return order === 'asc' ? -1 : 1;
        if (aValue > bValue) return order === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return {
      data: sortedData,
      pagination: rawData.pagination,
    };
  }, [rawData, orderBy, order]);

  const handleClick = (user: UserListItem) => {
    window.location.href = `/user/${user.id}`;
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing page size
  };

  const handleSort = (property: keyof UserListItem) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Add User button: use can_add_user from list API (users:add-member permission)
  const canAddUser = isShowingAdvancedResults
    ? (advancedSearchData?.can_add_user ?? false)
    : (rawData?.can_add_user ?? false);

  const clearFilters = () => {
    setSearch('');
    setDelaySearch('');
    setUserType('all-members');
    setSpd('');
    setPage(0);
    if (isShowingAdvancedResults) {
      setIsShowingAdvancedResults(false);
      setAdvancedSearchData(null);
    }
  };

  const hasActiveFilters =
    search || userType !== 'all-members' || spd || isShowingAdvancedResults;

  const runAdvancedSearch = useCallback(
    async (
      form: {
        first_name: string;
        last_name: string;
        member_id: string;
        email: string;
        phone: string;
      },
      pageNum: number,
      perPageNum: number
    ) => {
      const hasAny =
        (form.first_name ?? '').trim() !== '' ||
        (form.last_name ?? '').trim() !== '' ||
        (form.member_id ?? '').trim() !== '' ||
        (form.email ?? '').trim() !== '' ||
        (form.phone ?? '').trim() !== '';
      if (!hasAny) return;
      setAdvancedSearchLoading(true);
      try {
        const res = await advancedSearchUsers({
          first_name: form.first_name?.trim() || undefined,
          last_name: form.last_name?.trim() || undefined,
          member_id: form.member_id?.trim() || undefined,
          email: form.email?.trim() || undefined,
          phone: form.phone?.trim() || undefined,
          page: pageNum,
          per_page: perPageNum,
        });
        const transformedData: UserListItem[] = res.data.map(
          (record: UserSearchRecord) => ({
            id: record.id,
            first_name: record.first_name,
            last_name: record.last_name,
            user_type: formatMemberStatus(record.member_status),
            last_chapter: record.current_chapter || '',
          })
        );
        setAdvancedSearchData({
          data: transformedData,
          pagination: res.pagination,
          can_add_user: res.can_add_user,
        });
      } finally {
        setAdvancedSearchLoading(false);
      }
    },
    []
  );

  const handleAdvancedSearchSubmit = () => {
    const form = {
      first_name: advFirstName.trim(),
      last_name: advLastName.trim(),
      member_id: advMemberId.trim(),
      email: advEmail.trim(),
      phone: advPhone.trim(),
    };
    const hasAny =
      form.first_name !== '' ||
      form.last_name !== '' ||
      form.member_id !== '' ||
      form.email !== '' ||
      form.phone !== '';
    if (!hasAny) return;
    setAdvancedSearchForm(form);
    setAdvancedSearchPage(0);
    setAdvancedSearchRowsPerPage(rowsPerPage);
    runAdvancedSearch(form, 0, rowsPerPage).then(() => {
      setIsShowingAdvancedResults(true);
      setAdvancedSearchModalOpen(false);
    });
  };

  const handleAdvancedSearchPageChange = (newPage: number) => {
    setAdvancedSearchPage(newPage);
    runAdvancedSearch(advancedSearchForm, newPage, advancedSearchRowsPerPage);
  };

  const handleAdvancedSearchRowsPerPageChange = (newRowsPerPage: number) => {
    setAdvancedSearchRowsPerPage(newRowsPerPage);
    setAdvancedSearchPage(0);
    runAdvancedSearch(advancedSearchForm, 0, newRowsPerPage);
  };

  // Helper function to get user type display name
  const getUserTypeDisplayName = (type: string) => {
    switch (type) {
      case 'all-members':
        return 'All-Members';
      case 'all':
        return 'Non-member and Members';
      case 'member-active':
        return 'Active Members';
      case 'member-inactive':
        return 'Inactive Members';
      case 'deceased':
        return 'Deceased';
      case 'non-member':
        return 'Non-Members';
      default:
        return type;
    }
  };

  // Helper function to get SPD display name
  const getSpdDisplayName = (spdId: string) => {
    if (!spdId || !spdList) return spdId;
    const spdItem = spdList.find((spd) => spd.id === spdId);
    return spdItem ? spdItem.name : spdId;
  };

  const columns: ServerSideTableColumn<UserListItem>[] = [
    {
      id: 'first_name',
      label: 'First Name',
      width: '25%',
      sortable: true,
    },
    {
      id: 'last_name',
      label: 'Last Name',
      width: '25%',
      sortable: true,
    },
    {
      id: 'user_type',
      label: 'Type',
      width: '25%',
      sortable: true,
    },
    {
      id: 'last_chapter',
      label: 'Chapter',
      width: '25%',
      sortable: true,
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Search and Basic Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              label="Search"
              id="search-field"
              size="small"
              value={delaySearch}
              fullWidth
              onChange={(event) => setDelaySearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <Button
              variant={showFilters ? 'contained' : 'outlined'}
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
              fullWidth
              size="small"
            >
              Filters
            </Button>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              disabled={!hasActiveFilters}
              fullWidth
              size="small"
            >
              Clear
            </Button>
          </Grid>

          {canAddUser && (
            <Grid item xs={12} sm={6} md={4}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateModalOpen(true)}
                fullWidth
                sx={{
                  backgroundColor: '#00A859',
                  '&:hover': { backgroundColor: '#008a4a' },
                }}
              >
                Add User
              </Button>
            </Grid>
          )}
        </Grid>

        {hasAdvancedSearchPermission && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={() => setAdvancedSearchModalOpen(true)}
              size="small"
            >
              Advanced Search
            </Button>
          </Box>
        )}

        {/* Advanced Filters */}
        {showFilters && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="user-type-label">User Type</InputLabel>
                  <Select
                    labelId="user-type-label"
                    value={userType}
                    label="User Type"
                    onChange={(event) => setUserType(event.target.value)}
                  >
                    <MenuItem value="all-members">All-Members</MenuItem>
                    <MenuItem value="member-active">Active Members</MenuItem>
                    <MenuItem value="member-inactive">
                      Inactive Members
                    </MenuItem>
                    <MenuItem value="deceased">Deceased</MenuItem>
                    <MenuItem value="all">Non-members and Members</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <SPDSelector
                  spd={spd}
                  handleSpdSelect={setSpd}
                  keyId="user-list"
                  all={true}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Active Filters Display */}
      {(search || userType || spd || isShowingAdvancedResults) && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: 'action.hover' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Active Filters:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {isShowingAdvancedResults && (
              <Chip
                label="Advanced search"
                onDelete={() => {
                  setIsShowingAdvancedResults(false);
                  setAdvancedSearchData(null);
                }}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
            {search && (
              <Chip
                label={`Search: "${search}"`}
                onDelete={() => {
                  setSearch('');
                  setDelaySearch('');
                }}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
            <Chip
              label={`Type: ${getUserTypeDisplayName(userType)}`}
              onDelete={() => setUserType('all-members')}
              color="secondary"
              variant="outlined"
              size="small"
            />
            {spd && (
              <Chip
                label={`SPD: ${getSpdDisplayName(spd)}`}
                onDelete={() => setSpd('')}
                color="secondary"
                variant="outlined"
                size="small"
              />
            )}
          </Stack>
        </Paper>
      )}

      {/* Results Summary */}
      {data && data.data.length === 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No users found
            {search && ` matching "${search}"`}
            {userType !== 'all-members' &&
              ` (${getUserTypeDisplayName(userType)})`}
            {spd && ` in ${getSpdDisplayName(spd)}`}
          </Typography>
        </Box>
      )}

      <ServerSideTable
        data={
          isShowingAdvancedResults
            ? (advancedSearchData?.data ?? [])
            : (data?.data ?? [])
        }
        columns={columns}
        loading={isShowingAdvancedResults ? advancedSearchLoading : isFetching}
        page={isShowingAdvancedResults ? advancedSearchPage : page}
        rowsPerPage={
          isShowingAdvancedResults ? advancedSearchRowsPerPage : rowsPerPage
        }
        totalCount={
          isShowingAdvancedResults
            ? (advancedSearchData?.pagination?.total_count ?? 0)
            : (data?.pagination?.total_count ?? 0)
        }
        totalPages={
          isShowingAdvancedResults
            ? (advancedSearchData?.pagination?.total_pages ?? 0)
            : (data?.pagination?.total_pages ?? 0)
        }
        hasNext={
          isShowingAdvancedResults
            ? (advancedSearchData?.pagination?.has_next ?? false)
            : (data?.pagination?.has_next ?? false)
        }
        hasPrev={
          isShowingAdvancedResults
            ? (advancedSearchData?.pagination?.has_prev ?? false)
            : (data?.pagination?.has_prev ?? false)
        }
        order={order}
        orderBy={orderBy}
        onPageChange={
          isShowingAdvancedResults
            ? handleAdvancedSearchPageChange
            : handlePageChange
        }
        onRowsPerPageChange={
          isShowingAdvancedResults
            ? handleAdvancedSearchRowsPerPageChange
            : handleRowsPerPageChange
        }
        onSort={handleSort}
        onRowClick={handleClick}
        emptyMessage="No users found"
      />
      <UserCreateModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={(userId) => {
          setCreateModalOpen(false);
          // Optionally refresh the list or navigate
        }}
      />

      <Modal
        open={advancedSearchModalOpen}
        onClose={() => setAdvancedSearchModalOpen(false)}
        aria-labelledby="advanced-search-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 440 },
            bgcolor: 'background.paper',
            borderRadius: 1,
            boxShadow: 24,
            p: 3,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography id="advanced-search-modal-title" variant="h6">
              Advanced Search
            </Typography>
            <Button
              size="small"
              onClick={() => setAdvancedSearchModalOpen(false)}
              startIcon={<CloseIcon />}
            >
              Close
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Search by one or more fields. All fields are optional.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                label="First Name"
                value={advFirstName}
                onChange={(e) => setAdvFirstName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                label="Last Name"
                value={advLastName}
                onChange={(e) => setAdvLastName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                label="Member ID"
                value={advMemberId}
                onChange={(e) => setAdvMemberId(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                label="Email"
                type="email"
                value={advEmail}
                onChange={(e) => setAdvEmail(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                label="Phone (cell or home)"
                value={advPhone}
                onChange={(e) => setAdvPhone(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => setAdvancedSearchModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleAdvancedSearchSubmit}
                  disabled={
                    advancedSearchLoading ||
                    (!advFirstName.trim() &&
                      !advLastName.trim() &&
                      !advMemberId.trim() &&
                      !advEmail.trim() &&
                      !advPhone.trim())
                  }
                >
                  Search
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </Box>
  );
};

export default UserList;
